import React, { useState, useEffect } from 'react';
import {
  getBrokers,
  reassignOrgBroker,
  getOrgBrokerHistory,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { toast } from 'sonner';
import { Briefcase, Loader2, History, ArrowRight } from 'lucide-react';

/**
 * BrokerAssignment — super-admin-only section to reassign an org to a different broker.
 * Renders inside the Organization tab of Settings.jsx.
 */
export default function BrokerAssignment({ organization, onChanged }) {
  const [brokers, setBrokers] = useState([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);
  const [newBrokerId, setNewBrokerId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const currentBrokerId = organization?.broker_id || '';

  useEffect(() => {
    (async () => {
      try {
        const res = await getBrokers(false);
        setBrokers(res.data || []);
      } catch (e) {
        // Silent fail - permission gated by parent
      } finally {
        setLoadingBrokers(false);
      }
    })();
  }, []);

  useEffect(() => { setNewBrokerId(''); setReason(''); }, [organization?.id]);

  const loadHistory = async () => {
    try {
      const res = await getOrgBrokerHistory(organization.id);
      setHistory(res.data || []);
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  const handleReassign = async () => {
    if (!organization?.id) return;
    const targetId = newBrokerId === '__unassign__' ? null : newBrokerId;
    if (!newBrokerId) { toast.error('Select a new broker'); return; }
    if (targetId === currentBrokerId) { toast.error('Already assigned to this broker'); return; }
    setSaving(true);
    try {
      await reassignOrgBroker(organization.id, targetId, reason);
      toast.success('Broker reassigned');
      setReason('');
      setNewBrokerId('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassignment failed');
    } finally {
      setSaving(false);
    }
  };

  const currentBroker = brokers.find(b => b.id === currentBrokerId);
  const targetBroker = brokers.find(b => b.id === newBrokerId);

  return (
    <Card className="bg-card border-border" data-testid="broker-assignment-card">
      <CardHeader>
        <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" />
          Broker Assignment
        </CardTitle>
        <CardDescription>
          Reassign this organization to a different broker of record. The change is audit-logged.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm">
            <span className="text-muted-foreground">Current broker: </span>
            <span className="font-semibold" data-testid="current-broker">
              {currentBroker ? `${currentBroker.name} (${currentBroker.email})` : 'Unassigned'}
            </span>
          </div>
          <AlertDialog open={showHistory} onOpenChange={(o) => { setShowHistory(o); if (o) loadHistory(); }}>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="broker-history-btn">
                <History className="w-4 h-4 mr-1" /> History
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Broker reassignment history</AlertDialogTitle>
                <AlertDialogDescription>
                  Audit log of broker changes for {organization?.name}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="max-h-72 overflow-y-auto space-y-2 text-sm">
                {history.length === 0 ? (
                  <p className="text-muted-foreground">No history.</p>
                ) : history.map(h => (
                  <div key={h.id} className="p-2 rounded border border-border bg-secondary/30">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {new Date(h.changed_at).toLocaleString()} · by {h.changed_by_name}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span>{h.old_broker_name || 'Unassigned'}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span className="font-semibold">{h.new_broker_name || 'Unassigned'}</span>
                    </div>
                    {h.reason && <div className="text-xs text-muted-foreground mt-1">"{h.reason}"</div>}
                  </div>
                ))}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Close</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Reassign to broker</Label>
            <Select value={newBrokerId} onValueChange={setNewBrokerId} disabled={loadingBrokers}>
              <SelectTrigger className="bg-secondary/50" data-testid="new-broker-select">
                <SelectValue placeholder={loadingBrokers ? 'Loading…' : 'Select broker'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassign__">— Unassign —</SelectItem>
                {brokers.filter(b => b.id !== currentBrokerId).map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name} · {b.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Account moved to new producer"
              className="bg-secondary/50"
              data-testid="reassign-reason-input"
            />
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              disabled={!newBrokerId || saving}
              className="btn-primary-glow"
              data-testid="reassign-broker-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}
              Reassign Broker
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm broker reassignment</AlertDialogTitle>
              <AlertDialogDescription>
                {organization?.name} will be moved from{' '}
                <span className="font-semibold">{currentBroker ? currentBroker.name : 'Unassigned'}</span>{' '}
                to{' '}
                <span className="font-semibold">{newBrokerId === '__unassign__' ? 'Unassigned' : (targetBroker?.name || 'the selected broker')}</span>.
                This will be logged in the audit trail.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReassign} data-testid="confirm-reassign-broker-btn">
                Confirm reassignment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
